import { useForm, useWatch } from "react-hook-form";
import { useMutation } from "react-query";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react"; 
import TextFieldComponent from "../../components/inputs/Textfield/TextfieldComponent";
import { Card, Flex, Button, Spinner, Grid, Heading } from "@radix-ui/themes";
import { createUniversity } from "../../services/school/university/university";
import { UniversityType } from "../../types/University";
import TextAreaFieldComponent from "../../components/inputs/TextAreafield/TextAreafieldComponent";
import { ChevronLeftIcon, EnterIcon } from "@radix-ui/react-icons";
import { routes } from "../../routes";
import { yupResolver } from "@hookform/resolvers/yup";
import { UniversityFormValidation } from "./UniversityFormValidation";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import { provinces } from "../../utils/codes";

export default function CreateUniversity() {
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: createUniversity,
    mutationKey: "createUniversity",
  });
  const defaultValues: UniversityType = {
    description: "",
    province: "",
  };
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<UniversityType>({
    resolver: yupResolver(UniversityFormValidation),
    defaultValues,
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldUseNativeValidation: false,
  });

  const dateCreated = useWatch({ control, name: "dateCreated" });

  useEffect(() => {
    if (dateCreated) {
      const year = new Date(dateCreated).getFullYear();
      setValue("yearCreated", year, { shouldValidate: true });
    }
  }, [dateCreated, setValue]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      const result = await mutation.mutateAsync(data);
      if (result) {
        toast.success("Instituição de Ensino Superior criada!");
        navigate(routes.universities);
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });
  const handleBack = () => {
    navigate(routes.universities);
  };
  return (
  <form onSubmit={onSubmit} noValidate>
    <Card size="1">
      <Flex direction="column" gap="6">
        {/* Seção 1: Dados Institucionais */}
        <Flex direction="column" gap="4">
          <Heading size="4">Dados Institucionais</Heading>
          
          <Grid columns="2" gap="4">
            <TextFieldComponent
              name="description"
              label="Instituição de Ensino Superior"
              control={control}
              errors={errors}
              fieldProps={{ placeholder: "Nome da instituição" }}
              required
            />
            <TextFieldComponent
              name="abbreviation"
              label="Sigla"
              control={control}
              errors={errors}
              fieldProps={{ placeholder: "Ex: UEM, UP, ISCTEM" }}
            />
          </Grid>

          <Grid columns="3" gap="4">
            <TextFieldComponent
              name="yearCreated"
              label="Ano de Criação"
              control={control}
              errors={errors}
              fieldProps={{ placeholder: "Ex: 2009", type: "number" }}
            />
            <TextFieldComponent
              name="dateCreated"
              label="Data de Criação"
              control={control}
              errors={errors}
              fieldProps={{ type: "date" }}
            />
            <SelectComponent
              name="type"
              label="Tipo de Instituição"
              control={control}
              errors={errors}
              options={[
                { value: "1", label: "Pública" },
                { value: "2", label: "Privada" }
              ]}
            />
          </Grid>

          <Grid columns="2" gap="4">
            <TextFieldComponent
              name="nuit"
              label="Número Único de Identificação Tributária (NUIT)"
              control={control}
              errors={errors}
              fieldProps={{ placeholder: "Número Único de Identificação Tributária (NUIT)" }}
            />
            <TextFieldComponent
              name="code"
              label="Código"
              control={control}
              errors={errors}
              fieldProps={{ placeholder: "Código" }}
            />
          </Grid>

          <Grid columns="2" gap="4">
            <SelectComponent
              name="province"
              label="Província"
              control={control}
              errors={errors}
              options={provinces?.map((u) => ({
                value: u.id ?? "",
                label: u.description ?? "",
              }))}
              required= {true}
            />
            <TextFieldComponent
              name="district"
              label="Distrito"
              control={control}
              errors={errors}
              fieldProps={{ placeholder: "Distrito" }}
            />
          </Grid>

          <Grid columns="2" gap="4">
            <TextFieldComponent
              name="email"
              label="Email Institucional"
              control={control}
              errors={errors}
              fieldProps={{ type: "email", placeholder: "geral@universidade.ac.mz" }}
            />
            <TextFieldComponent
              name="website"
              label="Website"
              control={control}
              errors={errors}
              fieldProps={{ placeholder: "https://www.exemplo.ac.mz" }}
            />
          </Grid>

          <TextAreaFieldComponent
            name="address"
            label="Endereço"
            control={control}
            errors={errors}
            fieldProps={{ placeholder: "Endereço completo" }}
          />
        </Flex>

        {/* Seção 2: Contatos do Dirigente */}
        <Flex direction="column" gap="4">
          <Heading size="4">Contatos do Dirigente</Heading>
          
          <Grid columns="3" gap="4">
            <TextFieldComponent
              name="dirigentFullname"
              label="Nome Completo"
              control={control}
              errors={errors}
              fieldProps={{ placeholder: "Nome do Dirigente" }}
            />
            <TextFieldComponent
              name="dirigentPhoneNumber"
              label="Telefone"
              control={control}
              errors={errors}
              fieldProps={{ placeholder: "Contacto do dirigente" }}
            />
            <TextFieldComponent
              name="dirigentEmail"
              label="Email"
              control={control}
              errors={errors}
              fieldProps={{ type: "email", placeholder: "Email do dirigente" }}
            />
          </Grid>
        </Flex>

        {/* Seção 3: Contatos do Responsável */}
        <Flex direction="column" gap="4">
          <Heading size="4">Contatos do Responsável</Heading>
          
          <Grid columns="3" gap="4">
            <TextFieldComponent
              name="responsableFullname"
              label="Nome Completo"
              control={control}
              errors={errors}
              fieldProps={{ placeholder: "Nome do Responsável" }}
            />
            <TextFieldComponent
              name="responsablePhoneNumber"
              label="Telefone"
              control={control}
              errors={errors}
              fieldProps={{ placeholder: "Contacto do responsável" }}
            />
            <TextFieldComponent
              name="responsableEmail"
              label="Email"
              control={control}
              errors={errors}
              fieldProps={{ type: "email", placeholder: "Email do responsável" }}
            />
          </Grid>
        </Flex>
        <Flex direction="column" gap="4">
          <Heading size="4">Outras Configurações</Heading>
             <TextFieldComponent
              name="minimumPassingGrade"
              label="Nota minima de aprovação"
              control={control}
              errors={errors}
              fieldProps={{ type: "number", placeholder: "Nota minima de aprovação" }}
            />
        </Flex>

        {/* Botões de Ação */}
        <Flex gap="4" justify="end">
          <Button color="red" onClick={handleBack} disabled={mutation.isLoading}>
            <ChevronLeftIcon /> Voltar
          </Button>
          <Button type="submit" disabled={mutation.isLoading} variant="surface">
            {mutation.isLoading ? (
              <Spinner loading>
                <EnterIcon />
              </Spinner>
            ) : (
              <>
                <EnterIcon /> Registar
              </>
            )}
          </Button>
        </Flex>
      </Flex>
    </Card>
  </form>
  );
}